/* eslint-disable import/prefer-default-export */
import ky from 'ky';
import { useCallback } from 'react';
import dayjs from 'dayjs';
import { useLocalStorageState } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import { useQueryClient } from '@tanstack/react-query';
import { publicClientMethods } from './services/core/index';

const REFRESH_TOKEN_INTERVAL = 55;

export const useAuthenticatedApiClient = (token?: string) => {
  const [account, setAccount] =
    useLocalStorageState<AccountPersistInfo>('account');
  const queryClient = useQueryClient();

  const updateCachedUserToken = useCallback(
    (newToken: string) => {
      queryClient.setQueryData(['account', 'userToken'], newToken);
    },
    [queryClient]
  );

  return ky.create({
    hooks: {
      beforeRequest: [
        async (request) => {
          let freshToken =
            queryClient.getQueryData(['account', 'userToken']) ??
            account?.userToken;
          if (
            dayjs(account?.tokenSetAt)
              .add(REFRESH_TOKEN_INTERVAL, 'minutes')
              .diff(dayjs()) <= 0
          ) {
            try {
              const auth = await publicClientMethods.loginUser({
                accountToken: account?.accountToken as string,
                username: account?.email as string,
                pin: account?.pin as string,
              });
              setAccount(
                (prev) =>
                  ({
                    ...prev,
                    userToken: auth.token,
                    tokenSetAt: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
                  } as AccountPersistInfo)
              );
              updateCachedUserToken(auth.token);
              freshToken = auth.token;
            } catch (error) {
              // Forcefully logout if token is invalid
              localStorage.removeItem('account');
              localStorage.removeItem('ledger-length');
              localStorage.removeItem('pre-accounting');
              window.location.reload();
            }
          }
          if (token) {
            setAccount(
              (prev) =>
                ({
                  ...prev,
                  userToken: token,
                  tokenSetAt: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
                } as AccountPersistInfo)
            );
            updateCachedUserToken(token);
          }
          request.headers.set('Authorization', `Token ${token || freshToken}`);
          const accountId = account ? String(account.id) : 'unknown';

          if (request.url.includes('app') && !request.url.includes('authorize'))
            request.headers.set('pos-account-id', accountId);
        },
      ],
      beforeError: [
        async (error) => {
          const { response } = error;
          let result;
          if (response && response.body) {
            result = await error.response.json();
            if (
              response.status === 401 &&
              !response.url.includes('fiscalization_check')
            ) {
              const auth = await publicClientMethods.loginUser({
                accountToken: account?.accountToken as string,
                username: account?.email as string,
                pin: account?.pin as string,
              });
              updateCachedUserToken(auth.token);
              setAccount(
                (prev) =>
                  ({
                    ...prev,
                    userToken: auth.token,
                    tokenSetAt: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
                  } as AccountPersistInfo)
              );
            }
          }
          return result;
        },
      ],
    },
  });
};
